/**
 * API Services
 * Updated to match backend API_ROUTES_FINAL.md specification.
 * Exports all API services for use throughout the application.
 * 
 * All variable names, logs, and comments are in English.
 */

import axiosClient from './axiosClient';
import API_ENDPOINTS from './endpoints';
import type {
  HealthResponse,
  VideoInfoRequest,
  VideoInfoResponse,
  DownloadRequest,
  DownloadResponse,
  StatusResponse,
  QueueStatsResponse,
  JobsListResponse,
  DeleteJobResponse,
  CleanupRequest,
  CleanupResponse,
  ErrorResponse
} from '../../types/api';

/**
 * Health API
 * Checks API health status
 */
export const healthApi = {
  /**
   * Check API health
   */
  checkHealth: async (): Promise<HealthResponse> => {
    const response = await axiosClient.get<HealthResponse>(API_ENDPOINTS.HEALTH);
    return response.data;
  }
};

/**
 * Video Information API
 * Fetches metadata for videos from various platforms
 */
export const videoInfoApi = {
  /**
   * Get video information
   * @param url Video URL
   * @param platform Platform (dailymotion, odnoklassniki, rumble, vk)
   */
  getVideoInfo: async (url: string, platform: string): Promise<VideoInfoResponse> => {
    const response = await axiosClient.post<VideoInfoResponse>(API_ENDPOINTS.INFO, {
      url,
      platform
    });
    return response.data;
  }
};

/**
 * Download API
 * Handles video download requests
 */
export const downloadApi = {
  /**
   * Create download job
   * @param url Video URL
   * @param platform Platform (dailymotion, odnoklassniki, rumble, vk)
   * @param format Video format (mp4, webm, etc.)
   * @param quality Video quality (720p, 1080p, etc.)
   */
  createDownloadJob: async (
    url: string, 
    platform: string, 
    format: string, 
    quality: string
  ): Promise<DownloadResponse> => {
    const response = await axiosClient.post<DownloadResponse>(API_ENDPOINTS.DOWNLOAD, {
      url,
      platform,
      format,
      quality
    });
    return response.data;
  },

  /**
   * Get download stream URL
   * @param jobId Job ID
   */
  getDownloadStreamUrl: (jobId: string): string => {
    return `${axiosClient.defaults.baseURL}${API_ENDPOINTS.DOWNLOAD_STREAM}/${jobId}`;
  }
};

/**
 * Status API
 * Checks status of download jobs
 */
export const statusApi = {
  /**
   * Get job status
   * @param jobId Job ID
   */
  getJobStatus: async (jobId: string): Promise<StatusResponse> => {
    const response = await axiosClient.get<StatusResponse>(`${API_ENDPOINTS.STATUS}/${jobId}`);
    return response.data;
  }
};

/**
 * Admin API
 * Administrative functions for monitoring and management
 */
export const adminApi = {
  /**
   * Get queue statistics
   */
  getQueueStats: async (): Promise<QueueStatsResponse> => {
    const response = await axiosClient.get<QueueStatsResponse>(API_ENDPOINTS.ADMIN.QUEUE_STATS);
    return response.data;
  },

  /**
   * Get jobs list
   * @param state Job state filter (optional)
   * @param limit Number of jobs to return (default: 50)
   * @param offset Offset for pagination (default: 0)
   */
  getJobs: async (
    state: string = 'all',
    limit: number = 50,
    offset: number = 0
  ): Promise<JobsListResponse> => {
    const response = await axiosClient.get<JobsListResponse>(API_ENDPOINTS.ADMIN.JOBS, {
      params: { state, limit, offset }
    });
    return response.data;
  },

  /**
   * Delete a job
   * @param jobId Job ID to delete
   */
  deleteJob: async (jobId: string): Promise<DeleteJobResponse> => {
    const response = await axiosClient.delete<DeleteJobResponse>(`${API_ENDPOINTS.ADMIN.JOBS}/${jobId}`);
    return response.data;
  },

  /**
   * Cleanup old jobs
   * @param maxAge Maximum age in milliseconds (default: 24 hours)
   */
  cleanupJobs: async (maxAge: number = 86400000): Promise<CleanupResponse> => {
    const response = await axiosClient.post<CleanupResponse>(API_ENDPOINTS.ADMIN.CLEANUP, {
      maxAge
    });
    return response.data;
  }
};

export default {
  healthApi,
  videoInfoApi,
  downloadApi,
  statusApi,
  adminApi
};
